import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button/Button';
import { RecordFile } from '../../services/boxFilesService';
import { formatFileDate, formatFileSize } from './formatFile';
import styles from './RecordFiles.module.scss';

export interface RecordFileRowProps {
    file: RecordFile;
    locale?: string;
    pending: boolean;
    onDownload: (file: RecordFile) => void;
    /** Omit to hide the Delete button. */
    onDelete?: (file: RecordFile) => void;
    /** Receives the row element so the list can move focus to its Download button. */
    rowRef?: (fileId: string, element: HTMLLIElement | null) => void;
}

export const RecordFileRow = ({ file, locale, pending, onDownload, onDelete, rowRef }: RecordFileRowProps) => {
    const { t } = useTranslation();
    const date = formatFileDate(file.modifiedAt ?? file.createdAt, locale);
    const size = formatFileSize(file.size);

    // aria-disabled (not disabled) keeps a pending button focusable, so keyboard focus isn't dropped to <body>.
    const pendingClass = pending ? styles.pendingButton : '';

    return (
        <li className={styles.row} ref={element => rowRef?.(file.id, element)}>
            <div className={styles.details}>
                <span className={styles.name}>{file.name}</span>
                <span className={styles.meta}>
                    {[size, date].filter(Boolean).join(' · ')}
                </span>
            </div>
            <div className={styles.actions}>
                {/* aria-label starts with the visible word so voice control ("click Download") still matches. */}
                <Button
                    color="blue"
                    type="button"
                    style={pendingClass}
                    aria-disabled={pending}
                    onClick={() => { if (!pending) onDownload(file); }}
                    aria-label={t('RecordFiles.DownloadNamed', { defaultValue: 'Download {{name}}', name: file.name })}
                >
                    {t('RecordFiles.Download', 'Download')}
                </Button>
                {onDelete && (
                    <Button
                        color="red"
                        type="button"
                        style={pendingClass}
                        aria-disabled={pending}
                        onClick={() => { if (!pending) onDelete(file); }}
                        aria-label={t('RecordFiles.DeleteNamed', { defaultValue: 'Delete {{name}}', name: file.name })}
                    >
                        {t('RecordFiles.Delete', 'Delete')}
                    </Button>
                )}
            </div>
        </li>
    );
};
